import { apiRequest } from './client';
import type {
  CreatePortalUserInput,
  CreateTenantInput,
  DashboardSummary,
  PlatformInventory,
  PortalUser,
  Provider,
  Tenant,
  TenantDetail,
  UpdateTenantInput,
  UUID,
} from '@/types';

export const platformApi = {
  listTenants(): Promise<Tenant[]> {
    return apiRequest('/api/v1/platform/tenants');
  },

  getTenant(tenantId: UUID): Promise<TenantDetail> {
    return apiRequest(`/api/v1/platform/tenants/${tenantId}`);
  },

  createTenant(input: CreateTenantInput): Promise<Tenant> {
    return apiRequest('/api/v1/platform/tenants', { method: 'POST', body: input });
  },

  updateTenant(tenantId: UUID, input: UpdateTenantInput): Promise<Tenant> {
    return apiRequest(`/api/v1/platform/tenants/${tenantId}`, {
      method: 'PATCH',
      body: input,
    });
  },

  setTenantStatus(tenantId: UUID, status: Tenant['status']): Promise<Tenant> {
    return apiRequest(`/api/v1/platform/tenants/${tenantId}/status`, {
      method: 'PATCH',
      body: { status },
    });
  },

  deleteTenant(tenantId: UUID, confirmName: string): Promise<{ ok: boolean; id: UUID }> {
    return apiRequest(`/api/v1/platform/tenants/${tenantId}`, {
      method: 'DELETE',
      body: { confirm_name: confirmName },
    });
  },

  deleteTenantSalesforceOrg(
    tenantId: UUID,
    orgId: UUID
  ): Promise<{ ok: boolean; id: UUID }> {
    return apiRequest(`/api/v1/platform/tenants/${tenantId}/salesforce-orgs/${orgId}`, {
      method: 'DELETE',
    });
  },

  getInventory(): Promise<PlatformInventory> {
    return apiRequest('/api/v1/platform/inventory');
  },

  listUsers(params?: { tenantId?: string; limit?: number; offset?: number }): Promise<PortalUser[]> {
    const qs = new URLSearchParams();
    if (params?.tenantId) qs.set('tenantId', params.tenantId);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return apiRequest(`/api/v1/platform/users${query ? `?${query}` : ''}`);
  },

  createUser(input: CreatePortalUserInput): Promise<PortalUser> {
    return apiRequest('/api/v1/platform/users', { method: 'POST', body: input });
  },

  listProviders(): Promise<Provider[]> {
    return apiRequest('/api/v1/platform/providers');
  },

  getSystemHealth(): Promise<{ status: string; services: Record<string, unknown>[] }> {
    return apiRequest('/api/v1/platform/health');
  },

  getDashboard(): Promise<DashboardSummary> {
    return apiRequest('/api/v1/platform/dashboard');
  },
};
